'use strict';

/**
 * T315 Module Lock — LD-9 client-side warm primitive (the B2 root-cause fix).
 *
 * The MVP blocker ("Cannot setup PIN: module master not in session") is fixed
 * here: before any lock operation, ensure the module master(s) are present in
 * the in-memory session. If a module has no master yet, mint one, add it to
 * the in-memory moduleMasters, RE-SEAL the identity-vault inner blob under the
 * in-memory _identityMaster, and PUT the re-sealed ciphertext to the server.
 *
 * Custodian-of-ciphertext (LD-7): the re-seal happens entirely client-side;
 * the server stores ciphertext only. The plaintext password is NOT needed
 * (we use the in-memory _identityMaster, which is live after login).
 *
 * LD-10: this is the SINGLE provisioning path — it replaces the divergent
 * per-tab random `provisionMaster`. The identity-vault master is the source
 * of truth so two tabs never encrypt the same module under different masters.
 *
 * P1 batch: when N modules are missing, do ONE re-seal + ONE PUT — never N
 * sequential round-trips on the login critical path.
 *
 * Dependency-injected so it is testable in Node and reusable in the browser:
 *   getIdentityMaster() -> Uint8Array | null   (session._identityMaster)
 *   getModuleMasters()  -> object              (session.moduleMasters, mutated in place)
 *   randomBytes(n)      -> Uint8Array
 *   symmetricEncrypt(plaintext, key) -> envelope
 *   serialize(value)    -> JSON-safe (Uint8Array -> {__u8})
 *   getInner()          -> { privateKeys, moduleMasters, policies }
 *   putIdentityBin(vaultBin) -> Promise        (PUT /auth/vault/me/identity-bin)
 */

function _err(code, message) {
  const e = new Error(message);
  e.code = code;
  return e;
}

async function ensureModuleMasters(moduleIds, deps) {
  const ids = Array.isArray(moduleIds) ? moduleIds : [moduleIds];
  const masters = deps.getModuleMasters() || {};

  const missing = ids.filter((id) => !masters[id]);
  if (missing.length === 0) {
    return { provisioned: [] };
  }

  const identityMaster = deps.getIdentityMaster();
  if (!identityMaster) {
    // Post-reload / never-provisioned: _identityMaster was stripped on persist.
    // Caller must run the lightweight re-auth cold-warm fallback.
    throw _err('IDENTITY_MASTER_UNAVAILABLE',
      'Identity master not in memory — re-authenticate to provision module masters');
  }

  // Mint every missing master into the in-memory session FIRST.
  for (const id of missing) {
    masters[id] = deps.randomBytes(32);
  }

  // ONE re-seal of the inner blob carrying ALL masters (batch).
  const inner = deps.getInner();
  inner.moduleMasters = masters;
  const json = JSON.stringify(deps.serialize(inner));
  const vaultBin = deps.symmetricEncrypt(json, identityMaster);

  // ONE PUT.
  await deps.putIdentityBin(vaultBin);

  return { provisioned: missing.slice() };
}

module.exports = { ensureModuleMasters };
